/**
 * src/i18n/context/I18nProvider.tsx
 *
 * Reactive Translation Provider with in-memory hot-swapping,
 * fallback resolution (active -> en -> [missing:key]), and direction switching.
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { I18nContext, LanguageMeta, I18nContextType } from './I18nContext';
import { LanguagePack, LanguagePackValidator, CURRENT_I18N_SCHEMA_VERSION } from '../language-pack-validator';
import { SafeInterpolator } from '../services/SafeInterpolator';
import { DirectionSwitcher } from '../services/DirectionSwitcher';

// Default bundled English pack
const BASE_EN_PACK: LanguagePack = {
  code: 'en-US',
  name: 'English (US)',
  direction: 'ltr',
  version: '1.0.0',
  schemaVersion: CURRENT_I18N_SCHEMA_VERSION,
  translations: {
    'app.title': 'Multi-Protocol Sovereign Mesh',
    'app.tagline': 'Decentralized Microkernel Operating Platform',
    'auth.login': 'Log In',
    'auth.logout': 'Log Out',
    'auth.secretKey': 'Secret Key Activation',
    'auth.passkey': 'Passkey Authentication',
    'auth.placeholderKey': 'SOVEREIGN-BETA-2026',
    'auth.invalidKey': 'Invalid or expired invite code',
    'nav.dashboard': 'Dashboard',
    'nav.pos': 'POS Terminal',
    'nav.crm': 'CRM & Proposals',
    'nav.kanban': 'Kanban Taskflow',
    'nav.games': 'Games Arcade',
    'nav.marketplace': 'Marketplace',
    'nav.languages': 'Language Manager',
    'nav.settings': 'Settings',
    'profile.title': 'User Profile',
    'profile.avatar': 'Profile Avatar',
    'profile.displayName': 'Display Name',
    'profile.save': 'Save Changes',
    'qr.scanTitle': 'QR Code Scanner',
    'qr.displayTitle': 'QR Identity & Pairing',
    'common.cancel': 'Cancel',
    'common.confirm': 'Confirm',
    'common.close': 'Close',
    'common.search': 'Search...',
    'common.online': 'Online',
    'common.offline': 'Offline',
  },
};

// Seeded active packs
const SEED_PACKS: Record<string, LanguagePack> = {
  'en-US': BASE_EN_PACK,
  'ms': {
    code: 'ms',
    name: 'Bahasa Melayu',
    direction: 'ltr',
    version: '1.0.0',
    schemaVersion: CURRENT_I18N_SCHEMA_VERSION,
    translations: {
      'app.title': 'Rangkaian Berdaulat Pelbagai Protokol',
      'app.tagline': 'Platform Pengendalian Mikrokernel Teragih',
      'auth.login': 'Log Masuk',
      'auth.logout': 'Log Keluar',
      'auth.secretKey': 'Pengaktifan Kunci Rahsia',
      'auth.passkey': 'Pengesahan Kunci Laluan',
      'nav.dashboard': 'Papan Pemuka',
      'nav.pos': 'Terminal POS',
      'nav.crm': 'CRM & Cadangan',
      'nav.kanban': 'Aliran Tugas Kanban',
      'nav.games': 'Arked Permainan',
      'nav.marketplace': 'Pasaran Modul',
      'nav.languages': 'Pengurus Bahasa',
      'nav.settings': 'Tetapan',
      'profile.title': 'Profil Pengguna',
      'profile.displayName': 'Nama Paparan',
      'profile.save': 'Simpan Perubahan',
      'common.cancel': 'Batal',
      'common.confirm': 'Sahkan',
      'common.close': 'Tutup',
    },
  },
  'ar': {
    code: 'ar',
    name: 'العربية (Arabic)',
    direction: 'rtl',
    version: '1.0.0',
    schemaVersion: CURRENT_I18N_SCHEMA_VERSION,
    translations: {
      'app.title': 'الشبكة السيادية متعددة البروتوكولات',
      'app.tagline': 'منصة تشغيل النواة الدقيقة اللامركزية',
      'auth.login': 'تسجيل الدخول',
      'auth.logout': 'تسجيل الخروج',
      'auth.secretKey': 'تفعيل المفتاح السري',
      'nav.dashboard': 'لوحة القيادة',
      'nav.pos': 'نقطة البيع',
      'nav.crm': 'إدارة العملاء',
      'nav.games': 'ألعاب الميش',
      'nav.languages': 'مدير اللغات',
      'common.cancel': 'إلغاء',
      'common.confirm': 'تأكيد',
    },
  },
};

const STORAGE_KEY_CUSTOM_PACKS = 'mesh_custom_language_packs_v1';
const STORAGE_KEY_ACTIVE_LOCALE = 'mesh_active_locale_preference';

export const I18nProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [packs, setPacks] = useState<Record<string, LanguagePack>>(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY_CUSTOM_PACKS);
      if (stored) {
        const parsed = JSON.parse(stored);
        return { ...SEED_PACKS, ...parsed };
      }
    } catch {}
    return { ...SEED_PACKS };
  });

  const [activeCode, setActiveCode] = useState<string>(() => {
    try {
      return localStorage.getItem(STORAGE_KEY_ACTIVE_LOCALE) || 'en-US';
    } catch {
      return 'en-US';
    }
  });

  const [inactiveCodes, setInactiveCodes] = useState<Set<string>>(new Set());

  const currentPack = packs[activeCode] || packs['en-US'] || BASE_EN_PACK;
  const isRTL = currentPack.direction === 'rtl';

  useEffect(() => {
    DirectionSwitcher.setDirection(currentPack.direction);
  }, [currentPack.direction]);

  const setLanguage = useCallback(async (code: string): Promise<void> => {
    if (packs[code]) {
      setActiveCode(code);
      try {
        localStorage.setItem(STORAGE_KEY_ACTIVE_LOCALE, code);
      } catch {}
    }
  }, [packs]);

  const toggleLanguageActive = useCallback((code: string, isActive: boolean) => {
    setInactiveCodes((prev) => {
      const next = new Set(prev);
      if (isActive) {
        next.delete(code);
      } else {
        next.add(code);
      }
      return next;
    });
  }, []);

  const t = useCallback(
    (key: string, params?: Record<string, string | number | boolean | null | undefined>): string => {
      const activeDict = currentPack.translations;
      const baseDict = packs['en-US']?.translations || BASE_EN_PACK.translations;

      let rawString = activeDict[key];
      if (!rawString && activeCode !== 'en-US') {
        rawString = baseDict[key];
      }

      if (!rawString) {
        // Missing key fallback
        return import.meta.env?.DEV ? `[missing:${key}]` : key;
      }

      return SafeInterpolator.interpolate(rawString, params);
    },
    [currentPack, packs, activeCode]
  );

  const importLanguagePack = useCallback((rawJson: string | object) => {
    try {
      const parsed = typeof rawJson === 'string' ? JSON.parse(rawJson) : rawJson;
      const validation = LanguagePackValidator.validate(parsed);

      if (!validation.valid || !validation.sanitizedPack) {
        return { success: false, errors: validation.errors, warnings: validation.warnings };
      }

      const newPack = validation.sanitizedPack;
      setPacks((prev) => {
        const next = { ...prev, [newPack.code]: newPack };
        try {
          const customPacksOnly: Record<string, LanguagePack> = {};
          for (const [k, v] of Object.entries(next)) {
            if (!SEED_PACKS[k]) customPacksOnly[k] = v;
          }
          localStorage.setItem(STORAGE_KEY_CUSTOM_PACKS, JSON.stringify(customPacksOnly));
        } catch {}
        return next;
      });

      return { success: true, warnings: validation.warnings };
    } catch (err: any) {
      return { success: false, errors: [`JSON Parse error: ${err.message}`] };
    }
  }, []);

  const exportLanguagePack = useCallback((code: string): LanguagePack | null => {
    return packs[code] || null;
  }, [packs]);

  const getPack = useCallback((code: string): LanguagePack | undefined => {
    return packs[code];
  }, [packs]);

  const installedLanguages = useMemo<LanguageMeta[]>(() => {
    return Object.values(packs).map((p) => ({
      code: p.code,
      name: p.name,
      direction: p.direction,
      version: p.version,
      isActive: !inactiveCodes.has(p.code),
    }));
  }, [packs, inactiveCodes]);

  const value: I18nContextType = useMemo(
    () => ({
      currentLanguage: activeCode,
      isRTL,
      installedLanguages,
      t,
      setLanguage,
      toggleLanguageActive,
      importLanguagePack,
      exportLanguagePack,
      getPack,
    }),
    [activeCode, isRTL, installedLanguages, t, setLanguage, toggleLanguageActive, importLanguagePack, exportLanguagePack, getPack]
  );

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
};
